import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { CreateSalesOrderRequestDto } from "../models/orders/createSalesOrderRequestDto";
import { CreateSalesOrderResponseDto } from "../models/orders/createSalesOrderResponseDto";
import { CustomerPaymentMethodDto } from "../models/orders/customerPaymentMethodDto";
import { GetBacForSalesOrderDto } from "../models/orders/getBacForSalesOrderDto";
import { GetBCForSalesOrderDto } from "../models/orders/getBCForSalesOrderDto";
import { GetIRBillingReviewStatusResposne } from "../models/orders/getIRBillingReviewStatusResposne";
import { Contact } from "../models/shared/contact";
import { FileUploadDto } from "../models/shared/fileUploadDto";
import { GenericResponseDto } from "../models/generic-response.dto";
import { VendorInformationForPOEmailDto } from "../models/orders/vendorInformationForPOEmailDto";
import { ApiCacheService } from "./api-cache.service";

@Injectable({
  providedIn: "root",
})
export class SaleOrderService {
  private saleOrderManagementBaseURL =
    environment.baseUrl + "/SalesOrderManagement";
  private userManagementBaseURL = environment.baseUrl + "/User";
  private userSupportManagementBaseURL = environment.baseUrl + "/UserSupport";
  private clientManagementBaseURL = environment.baseUrl + "/ClientManagement";
  private d365SynchroniserBaseURL = environment.baseUrl + "/D365Synchroniser";
  private fileManagementBaseURL = environment.baseUrl + "/fileManagement";
  private invoiceReviewBaseURL = environment.baseUrl + "/InvoiceReview";
  private reportBaseURL = environment.baseUrl + "/Reports";

  private readonly getMethodPaymentListUrl =
    this.d365SynchroniserBaseURL + "/GetCustomerPaymentMethods?odataFilter=";
  private readonly getMethodPaymentListTtl = 8 * 60 * 60 * 1000; // 8 hours

  constructor(
    private _http: HttpClient,
    private _apiCache: ApiCacheService
  ) {}

  addSaleOrderHeaderData(
    payload: CreateSalesOrderRequestDto
  ): Observable<GenericResponseDto<CreateSalesOrderResponseDto>> {
    return this._http.post<GenericResponseDto<CreateSalesOrderResponseDto>>(
      this.saleOrderManagementBaseURL + "/AddSalesOrderHeader",
      payload
    );
  }

  getAllContactsByAccountNumber(
    accountNumber: string,
    partyNumber: string
  ): Observable<GenericResponseDto<Contact[]>> {
    return this._http.get<GenericResponseDto<Contact[]>>(
      `${this.clientManagementBaseURL}/GetAllContactsByAccountNumber?AccountNumber=${accountNumber}&PartyNumber=${partyNumber}`
    );
  }

  getMethodPaymentList(
    oDataFilter: string
  ): Observable<GenericResponseDto<CustomerPaymentMethodDto[]>> {
    if (oDataFilter && oDataFilter !== "?") {
      return this._http.get<GenericResponseDto<CustomerPaymentMethodDto[]>>(
        this.d365SynchroniserBaseURL +
          `/GetCustomerPaymentMethods?odataFilter=${oDataFilter}`
      );
    } else {
      return this._apiCache.get<GenericResponseDto<CustomerPaymentMethodDto[]>>(
        `${this.getMethodPaymentListUrl}${oDataFilter}`,
        this.getMethodPaymentListTtl
      );
    }
  }

  getBCForSalesOrder(
    payload: string
  ): Observable<GenericResponseDto<GetBCForSalesOrderDto[]>> {
    return this._http.get<GenericResponseDto<GetBCForSalesOrderDto[]>>(
      this.userManagementBaseURL + `/GetBCForSalesOrder?customerId=${payload}`
    );
  }

  getBacForSalesOrder(
    payload: string
  ): Observable<GenericResponseDto<GetBacForSalesOrderDto[]>> {
    return this._http.get<GenericResponseDto<GetBacForSalesOrderDto[]>>(
      this.userSupportManagementBaseURL +
        `/GetBacForSalesOrder?personnelNumber=${payload}`
    );
  }

  getIRBillingReviewStatus(
    payload: string
  ): Observable<GenericResponseDto<GetIRBillingReviewStatusResposne>> {
    return this._http.get<GenericResponseDto<GetIRBillingReviewStatusResposne>>(
      this.invoiceReviewBaseURL +
        `/GetIRBillingReviewStatus?salesOrderId=${payload}`
    );
  }

  getBacmReportMonthly(): Observable<GenericResponseDto<boolean>> {
    return this._http.get<GenericResponseDto<boolean>>(
      this.reportBaseURL + `/GetBacmReportMonthly`
    );
  }

  getPoEmailAttachmentUrl(
    fileObj: FileUploadDto
  ): Observable<GenericResponseDto<string>> {
    return this._http.post<GenericResponseDto<string>>(
      this.fileManagementBaseURL + "/upload",
      fileObj
    );
  }

  getVendorInformationForPOEmail(
    poNumber: string
  ): Observable<GenericResponseDto<VendorInformationForPOEmailDto>> {
    return this._http.get<GenericResponseDto<VendorInformationForPOEmailDto>>(
      `${this.saleOrderManagementBaseURL}/GetVendorInformationForPOEmail?poNumber=${poNumber}`
    );
  }
}
